import React from "react";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { convertTo12HourFormat } from "../../utils/timeUtils";

export default function BookingDetailsHeader({ booking, onDownloadInvoice }) {
  const navigate = useNavigate();

  // Get status badge color
  const getStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-700";

    const statusLower = status.toLowerCase();
    if (statusLower === "completed") {
      return "bg-green-100 text-green-700";
    }
    if (statusLower === "cancelled") {
      return "bg-red-100 text-red-700";
    }
    if (statusLower === "pending" || statusLower === "confirmed" || statusLower === "assigned") {
      return "bg-blue-100 text-blue-700";
    }
    if (statusLower === "in_progress" || statusLower === "inprogress") {
      return "bg-yellow-100 text-yellow-700";
    }
    return "bg-gray-100 text-gray-700";
  };

  // Get status label
  const getStatusLabel = (status) => {
    if (!status) return "Unknown";
    const statusLower = status.toLowerCase();
    if (statusLower === "inprogress") return "In Progress";
    if (statusLower === "in_progress") return "In Progress";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="max-w-[1180px] mx-auto px-4 lg:px-8 py-4">
      <div className="bg-white rounded-xl shadow-md p-6 flex items-start justify-between">
        {/* LEFT SIDE */}
        <div>
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => navigate("/")}
              className="text-gray-700 hover:text-gray-900 transition text-lg"
              title="Go back"
            >
              <FaArrowLeft />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              Booking Details
            </h2>
          </div>

          {/* Booking ID */}
          <p className="text-lg font-semibold text-gray-900">
            Booking #{booking?.id || "N/A"}
          </p>

          {/* Service Type */}
          <p className="text-gray-600 mt-1">
            <span className="font-medium">
              {booking?.serviceType === "rental"
                ? "Local Rental"
                : booking?.serviceType === "airport"
                ? "Airport Transfer"
                : "Outstation"}
            </span>
            {booking?.cabType && (
              <>
                {" "}| <span className="text-blue-600 font-medium">{booking.cabType}</span>
              </>
            )}
          </p>

          {/* Booking Date & Time */}
          <p className="text-gray-500 text-sm mt-1">
            Booked on {booking?.date || "Date not specified"}
            {(() => {
              const rawTime = booking?.time || booking?.pickupTime;
              if (rawTime && rawTime !== "Not specified") {
                const formatted = convertTo12HourFormat(rawTime);
                return formatted ? ` at ${formatted}` : "";
              }
              if (booking?.createdAt) {
                try {
                  const d = new Date(booking.createdAt);
                  if (!isNaN(d.getTime())) {
                    return ` at ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                  }
                } catch (e) {}
              }
              return "";
            })()}
          </p>
        </div>

        {/* RIGHT SIDE */}
        <div className="flex flex-col items-end gap-14">
          {/* Status */}
          <span
            className={`${getStatusColor(
              booking?.status
            )} text-sm font-medium px-4 py-1 rounded-md`}
          >
            {getStatusLabel(booking?.status)}
          </span>

          {/* Download Invoice */}
          {booking?.status?.toLowerCase() === "completed" && (
            booking?.invoiceDownloaded ? (
              <span
                className="bg-gray-100 border border-gray-300 text-gray-400 px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium cursor-not-allowed select-none"
                title="Invoice has already been downloaded once"
              >
                <FaDownload size={14} />
                Invoice Downloaded
              </span>
            ) : (
              <button
                onClick={onDownloadInvoice}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition"
              >
                <FaDownload size={14} />
                Download Invoice
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
